import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  Box,
  Button,
  FieldTrigger,
  VisualPicker,
  ScrollArea,
  Skeleton,
  Text,
  useIsMobile,
  type Color,
  toGlyphName,
  type GlyphName,
  toastManager,
} from "@salestrak/ui";

import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import { useWorkflowDetail } from "#app/lib/hooks/use-workflow-detail";
import { useStagesDialogActions } from "#app/lib/stages-dialog-context";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsBackBar, SettingsBackBarClearance, SettingsPageGutter } from "#app/components/page-header";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { useAppData } from "#app/lib/data";
import { DangerZoneGroup } from "#app/components/settings/danger-zone";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { WorkflowUpdateCycleMenu } from "#app/components/updates/workflow-update-cycle-menu";
import { formatUpdateCycle } from "#app/lib/project-updates";
import { SystemMarker } from "#app/components/entity";
import { EditableField } from "#app/components/settings/editable-field";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { withPermission } from "#app/components/permissions/require-permission";
import { WORKFLOW_DEFAULT_GLYPH } from "#app/lib/entity-visuals";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";

function WorkflowEditPage() {
  const isMobile = useIsMobile();
  const { workflowId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { refetch: refetchAppData } = useAppData();
  const { updateWorkflow, setDefaultWorkflow, validateWorkflowKey } =
    useWorkflowMutations();

  const { workflow, stageCount, loading, error } = useWorkflowDetail(workflowId);
  const { openStagesDialog } = useStagesDialogActions();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [keyError, setKeyError] = useState("");

  const handleFieldSave = useCallback(
    async (fieldId: string, value: string) => {
      if (!workflowId) return;

      if (fieldId === "key") {
        const upper = value
          .replace(/[^a-zA-Z0-9]/g, "")
          .slice(0, 6)
          .toUpperCase();
        if (upper.length >= 3 && upper !== workflow?.key) {
          try {
            const available = await validateWorkflowKey(upper);
            if (!available) {
              setKeyError("Key already in use");
              return;
            }
            setKeyError("");
          } catch {
            return;
          }
        } else {
          setKeyError("");
        }
        await updateWorkflow(workflowId, { key: upper || null });
      } else {
        await updateWorkflow(workflowId, { [fieldId]: value });
        if (fieldId === "name") refetchAppData();
      }
    },
    [
      workflowId,
      updateWorkflow,
      validateWorkflowKey,
      workflow?.key,
      refetchAppData,
    ],
  );

  const workflowIcon = workflow?.icon.icon;
  const workflowColor = workflow?.icon.color;

  const handleIconChange = useCallback(
    async (icon: GlyphName) => {
      if (!workflowId || !workflowColor) return;
      await updateWorkflow(workflowId, {
        icon: { icon, color: workflowColor },
      });
      refetchAppData();
    },
    [workflowId, workflowColor, updateWorkflow, refetchAppData],
  );

  const handleColorChange = useCallback(
    async (color: Color) => {
      if (!workflowId || !workflowIcon) return;
      await updateWorkflow(workflowId, {
        icon: { icon: workflowIcon, color },
      });
      refetchAppData();
    },
    [workflowId, workflowIcon, updateWorkflow, refetchAppData],
  );

  const handleSetDefault = useCallback(async () => {
    if (!workflowId) return;
    try {
      const result = await setDefaultWorkflow(workflowId);
      // The page's own Default tag shows a success; a refusal says why.
      if (result?.__typename !== "Workflow") {
        toastManager.add({
          title: resultMessage(result) ?? "Failed to set default workflow",
          type: "error",
        });
      }
    } catch (err) {
      toastMutationError(err, "Failed to set default workflow");
    }
  }, [workflowId, setDefaultWorkflow]);

  const handleDeleted = useCallback(() => {
    setDeleteOpen(false);
    refetchAppData();
    replace("/settings/workflows");
  }, [replace, refetchAppData]);

  if (loading && !workflow) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: back bar and header geometry hold through the resolve;
          * only the workflow name is a bar. Groups mirror the settled run —
          * General (3 rows), Stages, Danger zone. */}
        <SettingsBackBar
          label="Workflows"
          onClick={() => replace("/settings/workflows")}
        />
        <Box fill="flex" overflow="auto">
          <SettingsBackBarClearance />
          <PageHeader centered pinnedBack>
            <PageHeaderTitle>
              <Skeleton delayed height={20} width={180} />
            </PageHeaderTitle>
          </PageHeader>
          <SettingsPageSkeleton
            groups={[{ rows: 3 }, { rows: 2, unlabeled: true }, { rows: 1 }]}
          />
        </Box>
      </Box>
    );
  }

  if (!workflow) {
    return <LoadErrorState noun="workflow" error={error ?? null} />;
  }

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar
        label="Workflows"
        onClick={() => replace("/settings/workflows")}
      />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle
            adornment={
              workflow.isDefault ? (
                <SystemMarker kind="default" side="bottom" subject="workflow" />
              ) : null
            }
          >
            {workflow.name}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            {/* Name & icon / Identifier */}
            <SettingsGroup label="General">
              <SettingsGroupRow label="Name & Icon">
                <Box direction="row" gap="sm" align="center" fill={isMobile ? "width" : undefined}>
                  <VisualPicker
                    icon={toGlyphName(workflow.icon.icon) ?? WORKFLOW_DEFAULT_GLYPH}
                    defaultIcon={WORKFLOW_DEFAULT_GLYPH}
                    color={(workflow.icon.color ?? "blue") as Color}
                    onIconChange={handleIconChange}
                    onColorChange={handleColorChange}
                    // Offered only while the glyph has left the kind's own.
                    onReset={
                      (workflow.icon.icon ?? WORKFLOW_DEFAULT_GLYPH) ===
                      WORKFLOW_DEFAULT_GLYPH
                        ? undefined
                        : () => void handleIconChange(WORKFLOW_DEFAULT_GLYPH)
                    }
                    size="sm"
                    variant="outline"
                  />
                  <Box fill={isMobile ? "flex" : undefined}>
                    <EditableField
                      fieldId="name"
                      label="Workflow name"
                      initialValue={workflow.name ?? ""}
                      onSave={handleFieldSave}
                      placeholder="Workflow name"
                      width={isMobile ? "full" : "xl"}
                    />
                  </Box>
                </Box>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Identifier"
                subtitle="Unique key for this Workflow"
              >
                <Box gap="xs" width={isMobile ? undefined : 232} fill={isMobile ? "width" : undefined}>
                  <EditableField
                    fieldId="key"
                    label="Workflow key"
                    initialValue={workflow.key ?? ""}
                    onSave={handleFieldSave}
                    placeholder="e.g. PRD"
                    width="full"
                  />
                  {keyError ? (
                    <Text size="xs" variant="destructive">
                      {keyError}
                    </Text>
                  ) : null}
                </Box>
              </SettingsGroupRow>
              {/* Already default: the header badge states it, so no row. */}
              {workflow.isDefault ? null : (
                <SettingsGroupRow
                  label="Set workflow as default"
                  subtitle="New Projects start in this Workflow"
                >
                  <Button variant="ghost" onClick={handleSetDefault}>
                    <Text>Set as default</Text>
                  </Button>
                </SettingsGroupRow>
              )}
            </SettingsGroup>

            {/* Project stages — opens the stage manager dialog (drafted
                edits, one Apply), the same surface the board and list headers
                open. The section is named for the facet ("Workflow", the
                Linear grammar), never the noun, so the label and the row
                don't state one fact twice. */}
            <SettingsGroup label="Workflow">
              <SettingsGroupRow
                label="Project stages"
                subtitle="Customize the stages a Project moves through"
                value={`${stageCount} stages`}
                onClick={() => openStagesDialog(workflowId)}
              />
              {/* The Workflow's default follow up cycle — the rung every Stage
                  and Project inherits until one pins its own. The menu is the
                  same ladder the board's Stage menu offers (no inherit row:
                  there is nothing above a Workflow); "Follow ups off" is the
                  Admin-only last row, gated inside the menu. */}
              <SettingsGroupRow
                label="Default follow up cycle"
                subtitle="Set how often Projects need a Follow up, unless a Stage sets its own"
              >
                {/* FieldTrigger: the Select field's dress, so the menu reads
                    like every other settings select. */}
                <WorkflowUpdateCycleMenu workflow={workflow}>
                  <FieldTrigger
                    aria-label="Default follow up cycle"
                    value={formatUpdateCycle(workflow.updateCycle)}
                    width="auto"
                  />
                </WorkflowUpdateCycleMenu>
              </SettingsGroupRow>
            </SettingsGroup>

            <DangerZoneGroup
              dangerZone={[
                {
                  id: "delete-workflow",
                  label: "Delete workflow",
                  subtitle:
                    "Permanently remove this workflow and its stages.",
                  buttonLabel: "Delete workflow...",
                  variant: "destructive",
                  onClick: () => setDeleteOpen(true),
                  disabled: workflow.isDefault,
                  disabledReason: "The default workflow can't be deleted",
                },
              ]}
            />

            <SettingsPageGutter />
          </Box>
        </Box>

        <DeleteWorkflowDialog
          workflow={deleteOpen ? workflow : null}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={handleDeleted}
          onManageWorkflow={(id) => replace(`/workflow/${id}`)}
        />
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/workflows/$workflowId/",
)({ component: withPermission("manage.edit_workflows", WorkflowEditPage) });
